import {
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth, appleProvider } from '../../../data/services/firebase';
import { firestoreService } from '../../../data/services/firestoreService';
import { cloudFunctionsService } from '../../../data/services/cloudFunctionsService';
import type { JsonObject } from '../../../data/types/json';
import { UserRemapper } from '@/domain/remappers/user/userRemapper';
import { userService } from '@/domain/services/user/userService';
import type { SaveUserResult } from '../../entities/auth/authResult';
import type { FirebaseLoginResult } from '../../entities/auth/firebaseLoginResult';
import { UserUtils } from '@/domain/utils/userUtils';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { e2eeKeyGenerator } from '@/domain/services/textEncryption/e2eeKeyGenerator';
import { EXCLUSIVE_SUBSCRIPTION_DEFAULT_DOLLARS } from '@/domain/constants/remoteConfigDefaults';

function generateUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export async function generateUsername(name: string): Promise<string> {
  // Create base username from name
  let baseUsername = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!baseUsername) baseUsername = 'user';

  // Check if username exists
  let username = baseUsername;
  let counter = 1;

  while (await checkUsernameExists(username)) {
    username = `${baseUsername}${counter}`;
    counter++;
  }

  return username;
}

export async function checkUsernameExists(username: string, currentUserId?: string): Promise<boolean> {
  try {
    return await firestoreService.usernameExists(username, currentUserId);
  } catch (error) {
    appLogger.error('Error checking username', 'AuthService.checkUsernameExists', { error: error as Error });
    return false;
  }
}

class AuthService {
  // Sign in with Google (matching Flutter GoogleSignIn with cache clearing).
  // Mirrors Flutter `AppAuthenticationService.signInWithGoogle`
  // (lib/services/app_authentication_service.dart:135). This step ONLY
  // performs the Firebase popup; the caller is responsible for invoking
  // `saveUser(firebaseUser)` afterwards — matching `login_screen.dart`'s
  // two-step `_login` shape.
  async signInWithGoogle(): Promise<FirebaseLoginResult> {
    try {
      // Create a fresh Google provider for each sign-in to avoid caching
      // This mimics the Flutter cache clearing behavior
      const freshGoogleProvider = new GoogleAuthProvider();
      freshGoogleProvider.addScope('email');
      freshGoogleProvider.addScope('profile');
      freshGoogleProvider.setCustomParameters({
        prompt: 'select_account'  // Always show account selection
      });

      const result = await signInWithPopup(auth, freshGoogleProvider);
      if (!result.user) {
        return { firebaseUser: null, error: 'No user information available after Google sign-in.' };
      }

      return { firebaseUser: result.user, error: null };
    } catch (error: any) {
      appLogger.error('Google Sign-In error', 'AuthService.signInWithGoogle', { error: error as Error });
      return { firebaseUser: null, error: 'Authentication failed!' };
    }
  }

  // Sign in with Apple (matching Flutter AppleSignIn). Two-step shape:
  // this method only performs the Firebase popup; the caller invokes
  // `saveUser(firebaseUser)` afterwards. See Flutter
  // `AppAuthenticationService.signInWithApple`
  // (lib/services/app_authentication_service.dart:337).
  async signInWithApple(): Promise<FirebaseLoginResult> {
    try {
      const result = await signInWithPopup(auth, appleProvider);
      if (!result.user) {
        return { firebaseUser: null, error: 'No user information available after Apple sign-in.' };
      }

      return { firebaseUser: result.user, error: null };
    } catch (error: any) {
      appLogger.error('Apple Sign-In error', 'AuthService.signInWithApple', { error: error as Error });
      appLogger.error('Error code: ' + error.code, 'AuthService.signInWithApple');
      appLogger.error('Error message: ' + error.message, 'AuthService.signInWithApple');

      // Provide more specific error messages
      if (error.code === 'auth/operation-not-allowed') {
        return { firebaseUser: null, error: 'Apple Sign-In is not properly configured. Please check Firebase and Apple Developer settings.' };
      } else if (error.code === 'auth/unauthorized-domain') {
        return { firebaseUser: null, error: 'This domain is not authorized for Apple Sign-In. Please add it to your Apple Developer configuration.' };
      }

      return { firebaseUser: null, error: 'Authentication failed!' };
    }
  }

  // Sign in with email and password (DEV-ONLY QA tooling — the form that
  // calls this is gated behind `import.meta.env.DEV`). Two-step shape mirrors
  // `signInWithGoogle`: this method only performs the Firebase sign-in; the
  // caller invokes `saveUser(firebaseUser)` afterwards. No Flutter parity
  // dimension — this is a web QA affordance only.
  async signInWithEmailAndPassword(email: string, password: string): Promise<FirebaseLoginResult> {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      if (!result.user) {
        return { firebaseUser: null, error: 'No user information available after email sign-in.' };
      }

      return { firebaseUser: result.user, error: null };
    } catch (error: any) {
      appLogger.error('Email Sign-In error', 'AuthService.signInWithEmailAndPassword', { error: error as Error });
      return { firebaseUser: null, error: 'Authentication failed!' };
    }
  }

  // Sign out (matching Flutter signOut behavior)
  async signOut() {
    try {
      // Sign out from Firebase Auth
      // Note: Google account selection is forced on next sign-in via 'select_account' prompt
      await signOut(auth);

      return { success: true, error: null };
    } catch (error: any) {
      appLogger.error('Error during sign out', 'AuthService.signOut', { error: error as Error });
      return { success: false, error: error.message };
    }
  }

  // Complete user creation flow matching Flutter's AppAuthenticationService.saveUser
  async saveUser(firebaseUser: FirebaseUser): Promise<SaveUserResult> {
    const result: SaveUserResult = {
      success: true,
      removing: false,
    };

    try {
      // Check if user document exists. fetchUserDetails returns null when
      // missing (mirrors Flutter's UsersRepository.getUserById → User?).
      const userDoc = await userService.fetchUserDetails(firebaseUser.uid);
      const documentExists = userDoc !== null;

      if (!documentExists || userDoc?.removed) {
        // Create new user using server-side cloud function (addUser)
        try {
          const userNameInfo = UserUtils.generateUserNameInfo(firebaseUser.displayName || '');
          const username = await generateUsername(userNameInfo.name);
          const now = Date.now();

          // Generate E2EE key pair (JWK format)
          const { publicJwk, privateJwk } = await e2eeKeyGenerator.generateE2EEKeys();

          // Create user object matching Flutter schema
          const user: any = {
            id: generateUuid(),
            firebase_id: firebaseUser.uid,
            firebaseId: firebaseUser.uid,
            publicJwk: publicJwk,
            country: navigator.language?.split('-')?.[1] || 'US',
            name: userNameInfo.name,
            first_name: userNameInfo.firstName,
            last_name: userNameInfo.lastName,
            firstLastNameLower: userNameInfo.firstLastNameJoinedLowerCase,
            lastFirstNameLower: userNameInfo.lastFirstNameJoinedLowerCase,
            profile_image: firebaseUser.photoURL?.replace('s96-c', 's192-c') || null,
            original_transaction_ids: userDoc?.originalTransactionIds || undefined,
            email: firebaseUser.email || '',
            created_timestamp: now,
            last_seen_timestamp: now,
            shareLink: generateUuid(),
            username: username,
            onboarded: true,
            // Flutter default User fields
            popularity: 0,
            contentCount: 0,
            blocked: false,
            pushToken: "",
            subscribers: 0,
            likes: 0,
            // Flutter seeds a new user document's subscription_charge from the
            // COMPILE-TIME constant, not the live Remote Config value:
            // `User({ this.subscriptionCharge = RemoteConfigsDefaults.exclusiveSubscriptionDefaultDollars })`
            // (lib/data/models/user.dart:97-98 → remote_configs_defaults.dart:19).
            subscription_charge: EXCLUSIVE_SUBSCRIPTION_DEFAULT_DOLLARS,
            reports: 0,
            contentReports: 0,
            suspended: false,
            removing: false,
            removed: false,
            blockedUsers: null,
            usersBlockedMe: null,
            mutedUsers: null,
            blockedConversations: null,
            type: null,
            prefferedHashTags: null,
            prefferedContentTags: null,
            hiddenContent: null,
            platform: null,
          };

          // Call cloud function addUser using CloudFunctionsService
          const success = await cloudFunctionsService.addUser(user as JsonObject, privateJwk as unknown as JsonObject);

          if (success) {
            result.user = UserRemapper.toEntity(user);
            result.success = true;
          } else {
            result.success = false;
          }
        } catch (error) {
          appLogger.error('Error creating new user (addUser)', 'AuthService.saveUser', { error: error as Error });
          result.success = false;
        }
      } else {
        // Existing user
        try {
          if (userDoc?.removing) {
            result.success = false;
            result.removing = true;
            return result;
          }

          // If user exists but missing encryption keys, set them via cloud function
          if (!userDoc?.publicJwk) {
            try {
              const { publicJwk, privateJwk } = await e2eeKeyGenerator.generateE2EEKeys();
              const setSuccess = await cloudFunctionsService.setUserEncryptionKeys(
                publicJwk as unknown as JsonObject,
                privateJwk as unknown as JsonObject,
              );
              if (setSuccess) {
                userDoc.publicJwk = publicJwk;
              }
            } catch (err) {
              appLogger.error('Failed to set user encryption keys', 'AuthService.saveUser', { error: err as Error });
            }
          }

          // userDoc is already a remapped UserEntity (UserRemapper.toEntity
          // ran inside userService.fetchUserDetails). Layer auth-specific
          // fallbacks for fields the user doc may have been missing.
          if (!userDoc.firebaseId) userDoc.firebaseId = firebaseUser.uid;
          if (!userDoc.email) userDoc.email = firebaseUser.email || '';
          if (!userDoc.createdTimestamp) userDoc.createdTimestamp = Date.now();
          result.user = userDoc;

          result.success = true;
        } catch (error) {
          appLogger.error('Error loading existing user', 'AuthService.saveUser', { error: error as Error });
          result.success = false;
        }
      }
    } catch (error) {
      appLogger.error('Error in saveUser', 'AuthService.saveUser', { error: error as Error });
      result.success = false;
    }

    return result;
  }

  // Auth state listener - returns only Firebase user ID
  // Full user data is fetched and stored by LoginPage, not here
  onAuthStateChanged(callback: (firebaseUserId: string | null) => void) {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        callback(firebaseUser.uid);
      } else {
        callback(null);
      }
    });
  }

  // Get current Firebase user
  getCurrentUser() {
    return auth.currentUser;
  }

  /**
   * Epoch-millis of when the CURRENT Firebase session last actually
   * authenticated, or `null` when unavailable.
   *
   * This is the "when did this session last sign in" signal, read from
   * Firebase Auth's `UserMetadata.lastSignInTime` (a UTC date string). It is
   * deliberately distinct from the user document's `last_seen_timestamp`,
   * which `LastSeenTimestampListener` bumps on every app-open and therefore
   * reflects "last active", not "last authenticated" — the Delete Account
   * 30-day staleness gate needs the latter.
   *
   * Returns `null` when there is no `currentUser` (signed out), no
   * `lastSignInTime`, or the timestamp fails to parse (`NaN`). Pure sync read.
   */
  getLastSignInTimestamp(): number | null {
    const lastSignInTime = auth.currentUser?.metadata.lastSignInTime;
    if (!lastSignInTime) {
      return null;
    }

    const parsed = Date.parse(lastSignInTime);
    return Number.isNaN(parsed) ? null : parsed;
  }
}

export const authService = new AuthService();